import { Component, ViewChild, ElementRef, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { Pros } from 'src/providers/pros/pros';
import { PastJobPdfParams, PastJobPdfProvider } from '../past-job-pdf/past-job-pdf';
import { restockTaskFromApi } from 'src/shared/services/restocking-orders.service';
import { ClientHub } from 'src/providers/client-hub/client-hub';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { Client } from 'src/providers/client/client';
import { MapPopup } from 'src/pages/dashboard/map-popup/map-popup';
import { popupOptions } from 'src/pages/dashboard/map-popup/popup-opts';
import { DynamicComponentProvider } from 'src/shared/providers/dynamic-component/dynamic-component';
import { Schedule } from 'src/providers/schedule/schedule';
import { animate, state, style, transition, trigger } from '@angular/animations';

declare const mapboxgl: any;

@Component({
    selector: 'past-shared-job',
    template: `
    <tidy-error-message
      [text]="errorMessage">
    </tidy-error-message>
    <div id="pastSharedJobMap" #map style="width: 400px; height: 300px; border-radius: 6px; position: absolute; left: -10000px; top: -10000px; z-index: -1;"></div>
    <ion-content *ngIf="printableHtml" class="shared-job-content-host">
      <div class="shared-job-content" [innerHTML]="printableHtml" (click)="handleInlineContentClick($event)"></div>
    </ion-content>
    <div class="splash-screen" [class.splash-hidden]="splashVisible === 'hidden'" [@fadeOut]="splashVisible">
      <div class="splash-content">
        <img src="assets/img/bowtie-white.svg" alt="Tidy" class="bowtie-icon">
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  `,
    styles: [`
    .shared-job-content-host {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      --background: white;
      z-index: 1;
    }

    .shared-job-content {
      max-width: 900px;
      margin: 0 auto;
      padding: 24px;
      background: white;
      color: #1D2125;
    }

    .marker {
      cursor: pointer;
    }

    .marker img {
      display: block;
      width: 40px;
      height: 40px;
    }

    .splash-screen {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: black;
      z-index: 9999;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .splash-hidden {
      pointer-events: none;
    }

    .splash-content {
      text-align: center;
    }

    .bowtie-icon {
      width: 80px;
      height: 80px;
      margin-bottom: 20px;
    }

    .loading-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
    }

    .loading-dots span {
      width: 8px;
      height: 8px;
      background-color:rgb(83, 83, 83);
      border-radius: 50%;
      animation: bounce 1.4s infinite ease-in-out;
    }

    .loading-dots span:nth-child(1) { animation-delay: -0.32s; }
    .loading-dots span:nth-child(2) { animation-delay: -0.16s; }

    @keyframes bounce {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1); }
    }
  `],
    animations: [
        trigger('fadeOut', [
            state('visible', style({ opacity: 1 })),
            state('hidden', style({ opacity: 0 })),
            transition('visible => hidden', [
                animate('300ms ease-in')
            ]),
            transition('hidden => visible', [
                style({ opacity: 0 }),
                animate('0ms')
            ])
        ])
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class PastSharedJobPage implements OnInit, OnDestroy {

  errorMessage = '';
  jobId: number;
  @ViewChild('map', {static: false}) mapElement: ElementRef;
  mapInstance: any;
  job: any;
  address: any;
  splashVisible = 'visible';
  printableHtml: SafeHtml | null = null;
  private printableHtmlRaw: string | null = null;

  constructor(
    private pros: Pros,
    private route: ActivatedRoute,
    private pastJobPdfProvider: PastJobPdfProvider,
    private clientHub: ClientHub,
    private toDos: ToDos,
    private client: Client,
    private dynamicComponentService: DynamicComponentProvider,
    private schedule: Schedule,
    private sanitizer: DomSanitizer
  ) {
  }
  async ngOnInit() {
    const uuid = this.route.snapshot.paramMap.get('uuid');

    try {
      const sharedLink = await this.clientHub.getSharedLink(uuid)
      this.jobId = sharedLink.shared_link_data.job.id;
      this.job = sharedLink.shared_link_data.job;
      this.address = sharedLink.shared_link_data.address;
      await this.buildMap();
      await new Promise(resolve => setTimeout(resolve, 3000));
      await this.renderSharedJobContent(sharedLink.shared_link_data);
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    } finally {
      this.splashVisible = 'hidden';
    }
  }

  ngOnDestroy() {
    this.dynamicComponentService.destroyAll();
  }

  parseSharedJobMedias(jobMedias = []) {
    return {
      beforePhotos: jobMedias.filter((media) => media.category === 'before_job' && media.media_format === 'photo'),
      afterPhotos: jobMedias.filter((media) => media.category === 'after_job' && media.media_format === 'photo')
    }
  }

  async buildMap() {
    try {
      if (!this.mapElement || !this.jobId) {
        await new Promise(resolve => setTimeout(resolve, 100));
        return this.buildMap();
      }

      const mapContainer = document.getElementById('pastSharedJobMap');
      if (mapContainer) {
        mapContainer.style.display = 'block';
      }

      this.mapInstance = this.createMap();

      await new Promise((resolve) => {
        this.mapInstance.on('load', () => {
          this.mapInstance.resize();
          resolve(true);
        });

        setTimeout(() => {
          resolve(true);
        }, 5000);
      });

      const moments = this.getMoments();
      this.addMomentsToMap(moments, this.mapInstance);
      this.addAddressToMap(this.mapInstance);
      this.setMapBounds(this.mapInstance, moments);

      await new Promise((resolve) => {
        this.mapInstance.once('idle', () => {
          this.mapInstance.resize();
          resolve(true);
        });
        setTimeout(() => {
          this.mapInstance.resize();
          resolve(true);
        }, 2000);
      });
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  createMap() {
    try {
      mapboxgl.accessToken = 'pk.eyJ1IjoidmljdG9yZmVycmF6IiwiYSI6ImNrdHVoNHc1bTIwYjYycHE1NmhjdzVtdzMifQ.OIqKrdlDh4fJH0g0MO1zLg';

      // Use the address coordinates as initial center if available
      const initialCenter = this.address ? [this.address.longitude, this.address.latitude] : [-74.006, 40.7128]; // Default to NYC

      const map = new mapboxgl.Map({
        container: 'pastSharedJobMap',
        style: 'mapbox://styles/mapbox/dark-v10', // Back to dark theme
        center: initialCenter,
        zoom: 12,
        maxZoom: 16,
        preserveDrawingBuffer: true,
        attributionControl: true,
        logoPosition: 'bottom-left'
      });

      map.on('style.load', () => {
        const language = localStorage.getItem('language');
        if (language && language !== 'en') {
          map.getStyle().layers.forEach((layer) => {
            if (layer.type === 'symbol' && layer.layout?.['text-field']) {
              map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', `name_${language}`], ['get', 'name']]);
            }
          });
        }
      });

      // Add error handling for map load failures
      map.on('error', (_e) => {
        this.errorMessage = 'Failed to load map. Please try again.';
      });

      return map;
    } catch (error) {
      this.errorMessage = 'Failed to initialize map. Please try again.';
      throw error;
    }
  }

  getMoments() {
    const moments: any = [];
    this.job.activity_timelines.map((activityTimeline) => {
      activityTimeline.activity_timeline_items.map((moment) => {
        if (moment.type === 'cleaning_update') {
          moments.push({
            latitude: moment.data.latitude,
            longitude: moment.data.longitude,
            body: '',
            image: this.schedule.getMomentImage(moment.data.data.type),
            mapIcon: this.schedule.getMomentImage(moment.data.data.type),
            header: moment.data.text
          });
        }
      });
    });
    return moments;
  }

  addMomentsToMap(moments, map) {
    moments.forEach((moment, _i) => {
      if (moment?.latitude) {
        const popupContent = this.dynamicComponentService.injectComponent(
          MapPopup, x => x.data = {
            image: moment.image,
            body: moment.body,
            header: moment.header,
            learnMore: ''
          }
        );
        this.createPopupAndMarker(map, popupContent, moment.mapIcon, moment);
      }
    });
  }

  addAddressToMap(map) {
    if (!this.address?.longitude || !this.address?.latitude) {
      console.error('Address missing coordinates:', this.address?.id);
      return;
    }
    const popupContent = this.dynamicComponentService.injectComponent(
      MapPopup, x => x.data = {
        image: 'assets/img/house.svg',
        body: '',
        header: this.address?.address_name || this.address.address1,
        learnMore: ''
      }
    );
    const addressIcon = 'assets/img/map-home-blue.svg';
    this.createPopupAndMarker(map, popupContent, addressIcon, this.address);
  }

  createPopupAndMarker(map, popupContent, mapIcon, location, customOpts = null) {
    if (!location?.longitude || !location?.latitude) {
      console.error('Location missing coordinates:', location);
      return;
    }
    const popup = new mapboxgl.Popup({...popupOptions, ...customOpts}).setDOMContent(popupContent);
    const el = document.createElement('div');
    el.className = 'marker';
    el.innerHTML = `<img src="${mapIcon}" />`;
    this.createMarkerInstance({map, el, longLat: [ location.longitude, location.latitude ]}, popup);
  }

  createMarkerInstance(obj, popup) {
    const {el, longLat, map} = obj;
    const marker = new mapboxgl.Marker(el);
    marker.getElement().addEventListener('click', () => {
      const flyToCoords = [...longLat];
      const getZoom = map.getZoom() ;
      const zoomOut = getZoom < 0;
      const lgnSum = zoomOut ? 200 / (5 * getZoom) : 50 / (2 ** getZoom);
      if (flyToCoords[1] + lgnSum > 90) {
        flyToCoords[1] = 90;
      } else if ((flyToCoords[1] + lgnSum < -90)) {
        flyToCoords[1] = -90;
      } else {
        flyToCoords[1] += lgnSum;
      }
      map.flyTo({
        center: flyToCoords
      })
    });
    marker.setLngLat(longLat).setPopup(popup).addTo(map);
  }

  setMapBounds(map, moments) {
    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([this.address.longitude, this.address.latitude]);
    moments.forEach((moment) => {
      if (moment.latitude) {
        bounds.extend([moment.longitude, moment.latitude]);
      }
    });
    map.fitBounds(bounds, { padding: 50, duration: 0 });
  }

  async renderSharedJobContent(sharedLinkData) {
    const { beforePhotos, afterPhotos } = this.parseSharedJobMedias(sharedLinkData.job_medias);

    const tips = sharedLinkData.homekeeper_job?.tips ?? [];
    const durations = sharedLinkData.homekeeper_job?.job_durations;

    let rooms = sharedLinkData.rooms;
    rooms = this.toDos.buildRoomIcons(rooms);
    rooms = await this.toDos.getMetadataFromFiles(rooms);

    if (sharedLinkData?.job?.activity_timelines) {
      sharedLinkData.job.activity_timelines = sharedLinkData?.job?.activity_timelines?.map((activity) => ({
        ...activity,
        updates: activity?.activity_timeline_items?.map((item) => ({
          ...item,
          sent_at: item?.data?.created_at,
        })) || []
      }));
    }

    const params: PastJobPdfParams = {
      isSharedJob: true,
      job: {
        ...sharedLinkData.job,
        homekeepers: sharedLinkData.homekeepers,
        booking: {
          bookable_service: {
            name: sharedLinkData.service_type_details.name,
          }
        }
      },
      invoice: null,
      pro: {
        homekeeper: sharedLinkData.homekeepers?.[0]?.name?.split(' ')[0]
      },
      tips: tips.filter((tip) => tip.category === 'tip'),
      parkingReimbursements: tips.filter((tip) => tip.category === 'parking_reimbursement'),
      activities: sharedLinkData?.job?.activity_timelines || null,
      billingInfo: this.parseBillingInfo(sharedLinkData?.billing_info),
      messages: null,
      rooms,
      jobId: sharedLinkData.job.id,
      totalTipsValue: tips.reduce((acc, tip) => acc + tip.amount, 0),
      address: {
        address1: sharedLinkData.address.address,
        address2: sharedLinkData.address.unit
      },
      proReportedDuration: durations ? this.pros.parseDurationIntoHoursAndMintes(durations.reported_by_homekeeper, 'proReportedDuration') : null,
      fromStartDuration: durations ? this.pros.parseDurationIntoHoursAndMintes(durations.from_start_to_end_moment, 'fromStartDuration') : null,

      beforePhotos,
      afterPhotos,
      restockTasks: (sharedLinkData.restock_tasks || []).map(restockTaskFromApi),
    };
    try {
      const html = await this.pastJobPdfProvider.buildPrintableHtml(params);
      const opened = await this.pastJobPdfProvider.openPrintWindow(html, {
        silentOnBlocked: true,
        hideDismiss: true,
      });
      if (!opened) {
        this.printableHtmlRaw = html;
        const inlineHtml = html.replace(/\sonclick="[^"]*"/gi, '');
        this.printableHtml = this.sanitizer.bypassSecurityTrustHtml(inlineHtml);
      }
    } catch (_err) {
      // Silent error handling - content render failure is non-fatal
    }
  }

  handleInlineContentClick(event: Event) {
    const anchor = (event.target as HTMLElement)?.closest?.('a');
    if (!anchor || !anchor.closest('.export-btn')) {return;}
    event.preventDefault();
    if (!this.printableHtmlRaw) {return;}
    this.pastJobPdfProvider.openPrintWindow(this.printableHtmlRaw, { hideDismiss: true });
  }

  parseBillingInfo(billingInfo) {
    if (!billingInfo || billingInfo?.length === 0) {return null;}

    const parsedBillingInfo = {
      ...billingInfo,
      ammountUsedSum: billingInfo.details.reduce((acc, detail) => acc + detail.amount_used, 0)
    }

    return parsedBillingInfo;
  }
}
